import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DashboardStatsSkeleton } from "@/components/dashboard/DashboardSkeleton";
import { TransactionApi } from "@/api/transaction.api";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LineChart,
  Line,
  CartesianGrid,
  AreaChart,
  Area,
} from "recharts";
import { MetricsCard } from "@/components/ui/metrics-card";
import { Tag, Handshake, CreditCard, BarChart2, Star, Zap } from "lucide-react";
import { format } from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";

const PERIODS = [
  { key: "LIFETIME", label: "Lifetime" },
  { key: "ONE_MONTH", label: "1 Month" },
  { key: "THREE_MONTHS", label: "3 Months" },
  { key: "SIX_MONTHS", label: "6 Months" },
  { key: "ONE_YEAR", label: "1 Year" },
];

// Utility to format numbers as 10k, 20k, etc.
function formatYAxisTick(value: number) {
  if (Math.abs(value) >= 1000) {
    return (value / 1000).toFixed(0) + "k";
  }
  return value.toString();
}

const XpandPointDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any>(null); // will hold generalStatsData
  const [currency, setCurrency] = useState<any>(null);
  const [selectedPeriod, setSelectedPeriod] = useState("THREE_MONTHS");
  const [chartMode, setChartMode] = useState("points"); // 'points' or 'usd'

  useEffect(() => {
    async function fetchStats() {
      setLoading(true);
      let payload: any = { clientId: 0 };
      try {
        const userInfo = JSON.parse(sessionStorage.getItem("userInfo") || "{}");
        if (userInfo?.roleCode === "LOYYAL_ADMIN") {
          payload = { clientId: null };
        } else if (
          userInfo?.roleCode === "PROGRAM_ADMIN" &&
          userInfo?.clientId
        ) {
          payload = { clientId: userInfo.clientId };
        }
        payload.programType = "TRANSFER";
        const res = await TransactionApi.getGeneralStats(payload);
        setStats(res?.data?.generalStatsData || null);
        setCurrency(res?.data?.currencyVO || null);
      } catch (e) {
        setStats(null);
        setCurrency(null);
        toast.error("Failed to fetch dashboard data.");
      } finally {
        setLoading(false);
      }
    }
    fetchStats();
  }, []);

  // Get the stats for the selected period
  const periodStats = stats?.[selectedPeriod] || {};

  return (
    <div className="space-y-10">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
        <div className="w-full sm:w-48">
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger>
              <SelectValue placeholder="Select Period" />
            </SelectTrigger>
            <SelectContent>
              {PERIODS.map((period) => (
                <SelectItem key={period.key} value={period.key}>
                  {period.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {loading ? (
        <DashboardStatsSkeleton />
      ) : stats ? (
        <>
          {/* Top Cards in a single row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <MetricsCard
              title="Transfer Partners"
              value={periodStats.totalTransferPrograms?.toLocaleString() ?? 0}
              icon={<Handshake className="h-5 w-5" />}
            />
            <MetricsCard
              title="Exchange Transactions"
              value={periodStats.totalTransactions?.toLocaleString() ?? 0}
              icon={<CreditCard className="h-5 w-5" />}
            />
            <MetricsCard
              title="Points Exchanged"
              value={periodStats.totalPointsRedeemed?.toLocaleString() ?? 0}
              icon={<Star className="h-5 w-5" />}
            />
            <MetricsCard
              title="Revenue"
              value={
                <>
                  {(periodStats.revenue ?? 0).toLocaleString()}{" "}
                  <span className="text-sm font-normal">
                    {currency?.iso3CurrencyCode || ""}
                  </span>
                </>
              }
              icon={<BarChart2 className="h-5 w-5" />}
            />
          </div>

          {/* Monthly Points Exchanged (Area Chart) */}
          <div className="mt-6 p-4 sm:p-6 bg-white rounded-lg shadow border">
            <h2 className="text-xl font-semibold mb-4">
              Points Exchanged Over Time
            </h2>
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={Object.entries(
                    periodStats?.monthlyTotalPointsTransferredBreakdown || {}
                  )
                    .map(([date, value]: any) => ({
                      date,
                      points: Number(value) || 0,
                    }))
                    .sort(
                      (a: any, b: any) =>
                        new Date(a.date).getTime() - new Date(b.date).getTime()
                    )}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                    tickFormatter={(value: any) => {
                      const d = new Date(value);
                      return isNaN(d.getTime()) ? value : format(d, "MMM yyyy");
                    }}
                  />
                  <YAxis tickFormatter={formatYAxisTick} fontSize={12} />
                  <Tooltip
                    formatter={(val: any) => [val, "Points"]}
                    labelFormatter={(label) => label}
                  />
                  <Area
                    type="monotone"
                    dataKey="points"
                    stroke="#2563eb"
                    fill="#93c5fd"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          {/* Monthly USD Value (Area Chart) */}
          <div className="mt-6 p-4 sm:p-6 bg-white rounded-lg shadow border">
            <h2 className="text-xl font-semibold mb-4">
              Revenue Exchange Over Time
            </h2>
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={Object.entries(
                    periodStats?.monthlyUSDValueBreakdown || {}
                  )
                    .map(([date, value]: any) => ({
                      date,
                      value: Number(value) || 0,
                    }))
                    .sort(
                      (a: any, b: any) =>
                        new Date(a.date).getTime() - new Date(b.date).getTime()
                    )}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                    tickFormatter={(value: any) => {
                      const d = new Date(value);
                      return isNaN(d.getTime()) ? value : format(d, "MMM yyyy");
                    }}
                  />
                  <YAxis tickFormatter={formatYAxisTick} fontSize={12} />
                  <Tooltip
                    formatter={(val: any) => [
                      `${Number(val).toLocaleString()} USD`,
                      "Value",
                    ]}
                    labelFormatter={(label) => label}
                  />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#16a34a"
                    fill="#86efac"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          {/* New vs Repeated Transactions (Stacked Area Chart) */}
          <div className="mt-6 p-4 sm:p-6 bg-white rounded-lg shadow border">
            <h2 className="text-xl font-semibold mb-4">
              New vs Repeated Transactions Over Time
            </h2>
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={Array.from(
                    new Set([
                      ...Object.keys(
                        periodStats?.monthlyRepeatedTransactions || {}
                      ),
                      ...Object.keys(periodStats?.monthlyNewTransactions || {}),
                    ])
                  )
                    .map((date: any) => ({
                      date,
                      repeatedTx:
                        Number(
                          (periodStats?.monthlyRepeatedTransactions || {})[date]
                        ) || 0,
                      newTx:
                        Number(
                          (periodStats?.monthlyNewTransactions || {})[date]
                        ) || 0,
                    }))
                    .sort(
                      (a: any, b: any) =>
                        new Date(a.date).getTime() - new Date(b.date).getTime()
                    )}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                    tickFormatter={(value: any) => {
                      const d = new Date(value);
                      return isNaN(d.getTime()) ? value : format(d, "MMM yyyy");
                    }}
                  />
                  <YAxis
                    tickFormatter={(v: any) => Number(v).toLocaleString()}
                    fontSize={12}
                  />
                  <Tooltip
                    formatter={(val: any, name: any) => [
                      Number(val).toLocaleString(),
                      name,
                    ]}
                    labelFormatter={(label) => label}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="repeatedTx"
                    name="Repeated"
                    stackId="1"
                    stroke="#22c55e"
                    fill="#86efac"
                    fillOpacity={0.9}
                    strokeWidth={2}
                    dot={false}
                  />
                  <Area
                    type="monotone"
                    dataKey="newTx"
                    name="New"
                    stackId="1"
                    stroke="#3b82f6"
                    fill="#bfdbfe"
                    fillOpacity={0.9}
                    strokeWidth={2}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center text-gray-500">No data available</div>
      )}
    </div>
  );
};

export default XpandPointDashboard;
